import { NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, Users, Money, FileText, ChartBar, Gear, SignOut, Lightning,
  Buildings, Clock,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";

const NAV = [
  { to: "/admin", label: "Admin Hub", icon: Buildings, id: "admin", adminOnly: true },
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, id: "dashboard" },
  { to: "/payroll", label: "Payroll Processing", icon: Money, id: "payroll" },
  { to: "/attendance", label: "Attendance", icon: Clock, id: "attendance", adminOnly: true },
  { to: "/employees", label: "Employees", icon: Users, id: "employees" },
  { to: "/payslips", label: "Payslips & EA Forms", icon: FileText, id: "payslips" },
  { to: "/reports", label: "Reports & Analytics", icon: ChartBar, id: "reports" },
  { to: "/settings", label: "Settings", icon: Gear, id: "settings" },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  return (
    <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 flex-col border-r border-[#E2E5EA] bg-[#F4F5F7] md:flex">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-500">
          <Lightning weight="fill" size={20} className="text-white" />
        </div>
        <div>
          <p className="font-heading text-base font-extrabold leading-none tracking-tight text-[#0F172A]">
            PayFlow HR
          </p>
          <p className="mt-1 text-[10px] uppercase tracking-widest text-[#6B7280]">Irving Resources</p>
        </div>
      </div>

      <nav className="mt-2 flex-1 space-y-1 px-3">
        {items.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`nav-${id}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-md border-l-2 px-3 py-2.5 text-sm transition-colors duration-200 ${
                isActive
                  ? "border-red-500 bg-red-500/10 text-red-500"
                  : "border-transparent text-[#9CA3AF] hover:bg-[#141414] hover:text-[#0F172A]"
              }`
            }
          >
            <Icon size={19} weight="regular" />
            <span className="font-medium">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#E2E5EA] p-3">
        <div className="flex items-center gap-3 rounded-md px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EEF1F5] text-xs font-semibold text-red-500">
            {initials(user?.name || "HR")}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-[#0F172A]">{user?.name || "HR Admin"}</p>
            <p className="truncate text-xs">
              <span className={`font-medium ${user?.role === "approver" ? "text-blue-400" : "text-red-500"}`}>
                {user?.role === "approver" ? "Approver" : "HR Admin"}
              </span>
            </p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            title="Sign out"
            className="rounded-md p-2 text-[#6B7280] transition-colors duration-200 hover:bg-[#EEF1F5] hover:text-red-400"
          >
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
};
